// PCM helpers for Otto Live Protocol v1 audio (packages/protocol audio.ts):
// raw PCM, signed 16-bit little endian, mono. Ported from ottos-consult
// src/consult/geminiAudio.js (see PORTED-FROM.md). Pure, so they run in tests.

/** Float32 samples in -1..1 to PCM16 bytes. The input is never modified. */
export function float32ToPcm16(samples: Float32Array): ArrayBuffer {
  const pcm = new Int16Array(samples.length);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index] ?? 0));
    pcm[index] = Math.round(sample * 0x7fff);
  }
  return pcm.buffer;
}

/** PCM16 bytes to Float32 samples. An odd trailing byte is ignored. */
export function pcm16ToFloat32(buffer: ArrayBuffer): Float32Array {
  const pcm = new Int16Array(buffer, 0, Math.floor(buffer.byteLength / 2));
  const out = new Float32Array(pcm.length);
  for (let index = 0; index < pcm.length; index += 1) out[index] = (pcm[index] ?? 0) / 32768;
  return out;
}

/**
 * Linear resample, used for Otto's 24 kHz voice into the playback context's
 * rate (usually 48 kHz). Same method as today's player path.
 */
export function resampleLinear(samples: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || samples.length === 0) return samples;
  const ratio = fromRate / toRate;
  const length = Math.max(1, Math.round(samples.length / ratio));
  const out = new Float32Array(length);
  for (let index = 0; index < length; index += 1) {
    const position = index * ratio;
    const before = Math.floor(position);
    const after = Math.min(samples.length - 1, before + 1);
    const weight = position - before;
    out[index] = (samples[before] ?? 0) * (1 - weight) + (samples[after] ?? 0) * weight;
  }
  return out;
}

/** Raw RMS of a frame, 0..1. The gate threshold (0.005) is documented as raw RMS. */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const value = samples[index] ?? 0;
    sum += value * value;
  }
  return Math.sqrt(sum / samples.length);
}

/** Meter level 0..1 (RMS times 4, clamped), the scale the old meters and mouth used. */
export function meterLevel(samples: Float32Array): number {
  return Math.min(1, rms(samples) * 4);
}

/** Loudness in a few bands across the frame, for the level bars. */
export function meterBands(samples: Float32Array, bandCount = 6): number[] {
  if (samples.length === 0) return Array.from({ length: bandCount }, () => 0);
  const size = Math.max(1, Math.floor(samples.length / bandCount));
  return Array.from({ length: bandCount }, (_, band) => {
    const start = band * size;
    const end = band === bandCount - 1 ? samples.length : Math.min(samples.length, start + size);
    return meterLevel(samples.subarray(start, end));
  });
}
